import { InjectionToken, type Type } from '@angular/core';

/**
 * A pluggable "where does the file come from" for the Import Wizard
 * (delivery 2026-039). The local drop zone is the default; every other source
 * is a tab provided through FILE_SOURCES, in core.module.ts, next to
 * IMPORT_WIZARD_COMPONENT_TOKEN. The wizard itself never learns the source: it
 * receives a job id from `start-from-source` exactly as from `start`.
 */

/** What a source component emits when the person picks a file. */
export interface SourceRef {
  source_type: string;
  ref: string;
  name: string;
  size_bytes: number | null;
  kind: 'geo' | 'tabular' | null;
}

export type ImportDoor = 'geo' | 'tabular';

/** Contract of a source component rendered inside the picker. */
export interface FileSourceComponent {
  accept: ImportDoor;
  picked: { subscribe(fn: (ref: SourceRef) => void): { unsubscribe(): void } };
  /** true while the wizard is being opened for a pick: the source disables its action (lote 24/09). */
  isLoading?: boolean;
  /** the repository in view, so the source can say what was already imported here */
  repositoryId?: string;
}

export interface FileSourceTab {
  type: string;
  label: string;
  icon?: string;
  component: Type<FileSourceComponent>;
  /** The backend decides by the token (GET /api/sources); the tab only shows what it may. */
  canUse(): Promise<boolean> | boolean;
}

export const FILE_SOURCES = new InjectionToken<FileSourceTab[]>('FILE_SOURCES');

/** Row shape of GET /api/sources/{type}/projects/{project_id}/files (schemas/file_source_schema.py). */
export interface SourceFileDto {
  ref: string;
  name: string;
  display_name: string | null;
  format: string | null;
  category: string;
  size_bytes: number | null;
  sha256: string | null;
  received_at: string;
  sender_name: string;
  crs: string | null;
  geometry_type: string | null;
  importable: boolean;
  reason: string | null;
  kind: 'geo' | 'tabular' | null;
  /** newest import of this file in scope, or null (backend, FILE_SOURCE_IMPORTED_SCOPE) */
  imported: ImportedDto | null;
}

export interface ImportedDto {
  job_id: string;
  status: string;
  at: string | null;
  by: string | null;
  repository_id: string;
  same_repository: boolean;
}

/** Answer of POST /api/sources/jobs/{job_id}/usage. */
export interface UsageReportDto {
  registered: boolean;
  reason: string | null;
  usage: Record<string, unknown> | null;
}

export interface SourcePageDto {
  items: SourceFileDto[];
  total: number;
  page: number;
  per_page: number;
  reason: string | null;
}

export interface SourceDto {
  type: string;
  label: string;
  capabilities: string[];
}
